import { filter, includes, isEmpty, map } from "lodash";
import moment from "moment";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import { TAGS_VISIBLE_IN_TYPE } from "src/pages/Workspaces/consts";
import { API_DATE_FORMAT } from "src/consts/global";
import {
  addLocalStorageItem,
  getLocalStorageItem,
} from "src/services/localStorage";
import { createPropertyOption } from "./createPropertyOption";

export const ORG_CHART_TEAMS_PROPERTIES = "org_chart_teams_properties";
export const ORG_CHART_PEOPLE_PROPERTIES = "org_chart_people_properties";
export const ORG_CHART_DIFF_DATES = "org_chart_diff_dates";

export const ORG_CHART_DEFAULT_PANEL_ID = "org_chart_default_panel_id";

/**
 * Generates a workspace-specific localStorage key for org chart diff dates.
 * @param {string|null|undefined} workspaceId - The workspace ID. If not provided, uses default key.
 * @returns {string} The localStorage key for storing diff dates.
 */
const getOrgChartDiffDatesKey = (workspaceId) => {
  if (!workspaceId) {
    return ORG_CHART_DIFF_DATES;
  }
  return `${ORG_CHART_DIFF_DATES}_${workspaceId}`;
};

export const storeOrgChartProperties = ({ entityType, value }) => {
  const storageKey =
    entityType === ENTITY_TYPES.PERSON
      ? ORG_CHART_PEOPLE_PROPERTIES
      : ORG_CHART_TEAMS_PROPERTIES;

  addLocalStorageItem(storageKey, value);
};

const filterIt = (tagConfig, entityType) => (option) => {
  const allowedToHaveTag =
    entityType === ENTITY_TYPES.PERSON
      ? "allowPersonToHaveTag"
      : "allowGroupToHaveTag";
  return (
    includes(
      tagConfig?.[option.value]?.visibleIn,
      TAGS_VISIBLE_IN_TYPE.OrgChart
    ) && tagConfig?.[option.value]?.[allowedToHaveTag]
  );
};

const getInitialOptions = (tagConfig, entityType) => {
  const isHighlighted =
    entityType === ENTITY_TYPES.PERSON ? "isPersonBadge" : "isGroupBadge";
  return map(
    filter(tagConfig, (tagType) => {
      return (
        includes(tagType?.visibleIn, TAGS_VISIBLE_IN_TYPE.OrgChart) &&
        tagType?.[isHighlighted]
      );
    }),
    createPropertyOption()
  );
};

export const getOrgChartPropertiesFromLocalStorage = ({ tagConfig }) => {
  const peopleProperties =
    getLocalStorageItem(ORG_CHART_PEOPLE_PROPERTIES) || [];
  const teamsProperties = getLocalStorageItem(ORG_CHART_TEAMS_PROPERTIES) || [];

  const peopleData = !isEmpty(peopleProperties)
    ? peopleProperties
    : getInitialOptions(tagConfig, ENTITY_TYPES.PERSON);

  const teamData = !isEmpty(teamsProperties)
    ? teamsProperties
    : getInitialOptions(tagConfig, ENTITY_TYPES.GROUP);

  return {
    properties: {
      peopleData: filter(peopleData, filterIt(tagConfig, ENTITY_TYPES.PERSON)),
      teamsData: filter(teamData, filterIt(tagConfig, ENTITY_TYPES.GROUP)),
    },
  };
};

/**
 * Retrieves persisted org chart diff dates from localStorage for a specific workspace.
 * Dates are stored as ISO strings and converted back to moment objects.
 * @param {string|null|undefined} workspaceId - The workspace ID. If not provided, uses default key.
 * @returns {{fromDate: moment.Moment|null, toDate: moment.Moment|null}} Object containing parsed dates or nulls.
 */
export const getOrgChartDiffDatesFromLocalStorage = (workspaceId) => {
  const storageKey = getOrgChartDiffDatesKey(workspaceId);
  const storedDates = getLocalStorageItem(storageKey);
  if (!storedDates) {
    return {
      fromDate: null,
      toDate: null,
    };
  }

  const { fromDate: fromDateStr, toDate: toDateStr } = storedDates;

  let fromDate = null;
  let toDate = null;

  if (fromDateStr) {
    const parsedFromDate = moment(fromDateStr, API_DATE_FORMAT, true);
    fromDate = parsedFromDate.isValid() ? parsedFromDate : null;
  }

  if (toDateStr) {
    const parsedToDate = moment(toDateStr, API_DATE_FORMAT, true);
    toDate = parsedToDate.isValid() ? parsedToDate : null;
  }

  return {
    fromDate,
    toDate,
  };
};

/**
 * Persists org chart diff dates to localStorage for a specific workspace.
 * Dates are stored as ISO strings. If both dates are null, removes the entry from storage.
 * @param {Object} params - Parameters object.
 * @param {moment.Moment|null} params.fromDate - The baseline date (from date) to store.
 * @param {moment.Moment|null} params.toDate - The comparison date (to date) to store.
 * @param {string|null|undefined} params.workspaceId - The workspace ID. If not provided, uses default key.
 */
export const storeOrgChartDiffDates = ({ fromDate, toDate, workspaceId }) => {
  const storageKey = getOrgChartDiffDatesKey(workspaceId);
  const datesToStore = {
    fromDate: fromDate?.isValid() ? fromDate.format(API_DATE_FORMAT) : null,
    toDate: toDate?.isValid() ? toDate.format(API_DATE_FORMAT) : null,
  };

  // If both dates are null, remove from storage
  if (!datesToStore.fromDate && !datesToStore.toDate) {
    const ls = window.localStorage;
    if (ls) {
      ls.removeItem(storageKey);
    }
  } else {
    addLocalStorageItem(storageKey, datesToStore);
  }
};
